import { useState, useEffect, useCallback, useRef } from 'react';

import { useGetClassAttendance } from './useAttendanceManagement';

interface ClassroomData {
    classroom: {
        classroomId: string;
        providerArn?: string | null;
        learningActivityID?: string | null;
    };
}

interface AttendanceState {
    allRecords: any[];
    isLoading: boolean;
}

interface UseAttendanceReturn {
    attendanceRecords: any[];
    error: any;
    isLoading: boolean;
    refetch: () => void;
}
/**
 * Hook to manage attendance data state. Uses the getAttendanceData hook to retrieve data
 * one page at a time until all pages are loaded. This allows us to display records to the user
 * while loading. We retrieve all data so the user can search all students without having to
 * manually click through all the pages.
 * @param classroom
 * @returns
 */
export const useAttendanceData = (classroom: ClassroomData | null): UseAttendanceReturn => {
    const [state, setState] = useState<AttendanceState>({
        allRecords: [],
        isLoading: !!classroom,
    });

    const currentCursor = useRef<string | null>(null);

    const {
        data: attendanceRecords,
        pageInfo,
        error,
        isLoading: apiIsLoading,
        refetch: queryRefetch,
    } = useGetClassAttendance(classroom, {
        cursor: currentCursor.current,
        pageSize: 100,
    });

    // Handle API responses and pagination
    useEffect(() => {
        // This API sends loading status. Need this guard clause to prevent incorrect
        // re-renders while records are returned
        if (apiIsLoading) {
            return;
        }
        if (error) {
            setState((prev) => ({
                ...prev,
                isLoading: false,
            }));
            return;
        }

        if (attendanceRecords && attendanceRecords.length > 0) {
            setState((prev) => ({
                ...prev,
                allRecords: [...prev.allRecords, ...attendanceRecords],
            }));

            if (pageInfo?.cursor) {
                currentCursor.current = pageInfo.cursor;
            } else {
                // At the moment the API does not return records & null next cursor.
                // It'll always go one more page (empty) so in practice we should never
                // hit this branch but leaving in as defensive programming for unaccounted edge cases
                // & potential API changes in the future
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                }));
            }
        } else {
            setState((prev) => ({
                ...prev,
                isLoading: false,
            }));
        }
    }, [attendanceRecords, pageInfo, error, apiIsLoading]);

    const refetch = useCallback(async () => {
        setState({
            allRecords: [],
            isLoading: true,
        });
        currentCursor.current = null;
        await queryRefetch();
    }, [queryRefetch]);

    return {
        attendanceRecords: state.allRecords,
        error,
        isLoading: state.isLoading,
        refetch,
    };
};
